import { TaskFieldsTableLayout } from "@jugl-web/domain-resources/tasks/components/TaskFieldsTableLayout";

import {
  convertSnakeCaseToReadableString as humanizeName,
  useTranslations,
  cx,
} from "@jugl-web/utils";
import { FC } from "react";
import { FormField } from "@jugl-web/rest-api";
import { Checkbox, Avatar } from "@jugl-web/ui-components";
import { Highlightify } from "@jugl-web/utils/utils/Highlightify";
import { Switch } from "@jugl-web/ui-components/cross-platform/Switch";
import { UserGeneralProfile } from "@jugl-web/domain-resources/users/components/UserGeneralProfile";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useCustomerFields } from "@jugl-web/domain-resources/customers/hooks/useCustomerFields";
import {
  CUSTOMER_FIELDS_TABLE_DIMENSIONS,
  alwaysVisibleCompanyFields,
  alwaysMandatoryPersonalFields,
  alwaysVisiblePersonalFields,
} from "../consts";
import { ReactComponent as JuglIcon } from "../assets/jugl.svg";

const { Row, Cell, CellText, ReorderIcon } = TaskFieldsTableLayout;

interface CustomerFieldsTableRowProps {
  field: FormField;
  entityId: string;
  searchQuery: string;
  variant: FormField["section"];
  isVisible?: boolean;
  isMandatory?: boolean;
  openManageFieldSidebar?: (field: FormField) => void;
  onMandatoryCheckboxClick: (e: React.SyntheticEvent, field: FormField) => void;
  onVisibilityClick?: (e: React.SyntheticEvent) => void;
}

export const CustomerFieldRow: FC<CustomerFieldsTableRowProps> = ({
  field,
  entityId,
  searchQuery,
  variant,
  isMandatory,
  isVisible,
  openManageFieldSidebar,
  onMandatoryCheckboxClick,
  onVisibilityClick,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({
      id: field.id,
    });
  const { t } = useTranslations();
  const {
    fieldTypeLabelAndIcon,
    defaultFieldTypeIcons: fieldTypeIcons,
    customFieldTypeIcons,
  } = useCustomerFields({ entityId });
  const style = {
    transform: CSS.Transform.toString({
      ...transform,
      x: 0,
      y: transform?.y || 0,
      scaleX: transform?.scaleX ?? 1,
      scaleY: transform?.scaleY ?? 1,
    }),
    transition,
  };

  const isAlwaysMandatory =
    variant === "personal" &&
    alwaysMandatoryPersonalFields.includes(field.name);

  const isAlwaysVisible =
    variant === "personal"
      ? alwaysVisiblePersonalFields.includes(field.name)
      : alwaysVisibleCompanyFields.includes(field.name);

  const defaultFieldNamesThatCanBeEdited = ["lead_source", "referred_by"];
  const canBeEdited =
    field.is_cust_field ||
    defaultFieldNamesThatCanBeEdited.includes(field.name);
  const isDraggable =
    ["first_name", "last_name"].includes(field.name) === false;
  return (
    <Row
      key={field.id}
      height={CUSTOMER_FIELDS_TABLE_DIMENSIONS.ROW_HEIGHT}
      isHoverable
      className="font-secondary"
      onClick={
        canBeEdited
          ? () => {
              openManageFieldSidebar?.(field);
            }
          : undefined
      }
      {...attributes}
      {...listeners}
      id={field.id}
      style={style}
    >
      <Cell
        width={CUSTOMER_FIELDS_TABLE_DIMENSIONS.REORDER_CELL_WIDTH}
        className="cursor-pointer text-grey-400"
        ref={setNodeRef}
      >
        {isDraggable && <ReorderIcon />}
      </Cell>
      <Cell
        width={CUSTOMER_FIELDS_TABLE_DIMENSIONS.NAME_CELL_WIDTH}
        className={cx("grow font-medium text-dark")}
      >
        <span className="flex items-center pr-2">
          {field.is_cust_field
            ? customFieldTypeIcons[field.type]
            : fieldTypeIcons[field.type]}{" "}
        </span>
        <div className="truncate" title={humanizeName(field.name)}>
          <Highlightify
            searchWord={searchQuery}
            highlightClassName="text-primary"
          >
            {humanizeName(field.name)}
          </Highlightify>
        </div>
      </Cell>
      <Cell
        width={CUSTOMER_FIELDS_TABLE_DIMENSIONS.STANDARD_CELL_WIDTH}
        className={cx("text-sm font-normal text-dark capitalize", {
          capitalize: field.is_cust_field,
        })}
      >
        {field.is_cust_field
          ? fieldTypeLabelAndIcon[field.type]?.name
          : t({
              id: "customers-page.default-field",
              defaultMessage: "Default field",
            })}
      </Cell>
      <Cell width={CUSTOMER_FIELDS_TABLE_DIMENSIONS.STANDARD_CELL_WIDTH}>
        {field.type === "dropdown" &&
          t(
            {
              id: "customers-page.values-table-count-label",
              defaultMessage:
                "{count} {count, plural, one {value} other {values}}",
            },
            {
              count: field.values?.length,
            }
          )}
      </Cell>
      <Cell
        width={CUSTOMER_FIELDS_TABLE_DIMENSIONS.VISIBILITY_CELL_WIDTH}
        onClick={
          isAlwaysVisible || field.read_only
            ? undefined
            : (e) => onVisibilityClick?.(e)
        }
        className="gap-2 text-sm"
      >
        <Switch
          isChecked={
            isVisible !== undefined
              ? isVisible
              : field.visibility || isAlwaysVisible
          }
          isDisabled={isAlwaysVisible || field.read_only}
        />
        {(
          isVisible !== undefined
            ? isVisible
            : field.visibility || isAlwaysVisible
        )
          ? t({
              id: "common.visible",
              defaultMessage: "Visible",
            })
          : t({
              id: "common.not-visible",
              defaultMessage: "Not visible",
            })}
      </Cell>
      <Cell
        width={CUSTOMER_FIELDS_TABLE_DIMENSIONS.MANDATORY_CELL_WIDTH}
        className="flex items-center justify-center"
        onClick={
          isAlwaysMandatory || field.read_only
            ? undefined
            : (e) => {
                e.stopPropagation();
                onMandatoryCheckboxClick?.(e, field);
              }
        }
      >
        <Checkbox
          isChecked={
            (isMandatory !== undefined ? isMandatory : field.required) ||
            isAlwaysMandatory
          }
          readOnly
          isDisabled={isAlwaysMandatory || field.read_only}
          className={cx(
            "checked:bg-gradients-success checked:hover:bg-gradients-success checked:focus:bg-gradients-success",
            "disabled:cursor-not-allowed disabled:border-secondary-200 disabled:checked:border-secondary-200 disabled:checked:bg-secondary-200 disabled:checked:hover:bg-secondary-200"
          )}
        />
      </Cell>
      <Cell width={CUSTOMER_FIELDS_TABLE_DIMENSIONS.STANDARD_CELL_WIDTH}>
        {field.created_by ? (
          <UserGeneralProfile entityId={entityId} userId={field.created_by}>
            {(user) => (
              <div className="flex items-center gap-2 overflow-hidden">
                <Avatar
                  size="sm"
                  username={user.safeProfile.displayName}
                  imageUrl={user.safeProfile.avatar}
                />
                <CellText className="text-[#707577]">
                  {user.safeProfile.displayName}
                </CellText>
              </div>
            )}
          </UserGeneralProfile>
        ) : (
          <div className="flex items-center gap-2">
            <JuglIcon className="shrink-0" />
            <CellText className="text-[#707577]">Jugl</CellText>
          </div>
        )}
      </Cell>
      <Cell
        width={CUSTOMER_FIELDS_TABLE_DIMENSIONS.ACTIONS_CELL_WIDTH}
        shouldGrow
      />
    </Row>
  );
};
